import React, { useContext, useState, useEffect, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';

import { CartContext } from '../../contexts/CartContext';
import { TmAddress } from '../../tscapi/model/account';
import { AccountUtils } from '../../tscapi/utils/account-utils';

import { AddressForm } from './address.form';
import { Loading } from './loading.component';

export const BillingAddress = () => {
    const { t } = useTranslation();
    const { cartData, updateBillingAddress, orderModificationMode, orderHistoryDetail } = useContext(CartContext);
    const [modalIsOpen, setModalOpen] = useState(false);
    const [cartBillingAddress, setCartBillingAddress] = useState<TmAddress>(null);
    const [isBillingSameAsShipping, setIsBillingSameAsShipping] = useState(false);
    const [billingAddressErrorMessage, setErrorMessage] = useState('');
    const [formState, setFormState] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [addressToEdit, setAddressToEdit] = useState<TmAddress>({
        Id: 0,
        FirstName: '',
        LastName: '',
        DayPhone: '',
        Street: '',
        AddressRef1: '',
        City: '',
        State: '',
        ZipCode: '',
        IsDefault: false,
        POBoxNumber: '',
        IsPOBox: false,
        SalesForceAddressId: '',
    });
    const openModal = () => {
        setErrorMessage('');
        setModalOpen(true);
    };
    const closeModal = () => {
        setModalOpen(false);
    };

    useEffect(() => {
        if (cartData && cartData.Buyer && cartData.Buyer.BillingAddress) {
            setCartBillingAddress(cartData.Buyer.BillingAddress);
            const billingAddress = cartData.Buyer.BillingAddress;
            const formattedPhone = AccountUtils.formatPhoneNumber(billingAddress);
            setPhoneNumber(formattedPhone);
            if (
                cartData.ShippingAddress &&
                cartData.ShippingAddress.Id &&
                cartData.Buyer.BillingAddress.Id == cartData.ShippingAddress.Id
            ) {
                setIsBillingSameAsShipping(true);
            } else {
                setIsBillingSameAsShipping(false);
            }
        }
    }, [cartData]);

    const updateCartBillingAddress = async () => {
        if (addressToEdit) {
            const cartAddressToEdit = AccountUtils.formatAddressData(addressToEdit);
            try {
                await updateBillingAddress(cartAddressToEdit);
                closeModal();
            } catch (error) {
                setErrorMessage(
                    AccountUtils.getErrorMessage(error, t('Unable to update your billing address. Please try again.')),
                );
            }
        }
    };
    useEffect(() => {
        if (formState == 'submit') {
            updateCartBillingAddress();
        }
    }, [formState]);

    const validateFormAndSubmit = () => {
        setFormState('validate');
    };

    return (
        <>
            <div className="billingAddressWrap noBorderTop">
                <div className="billingaddress__label">
                    <h3>{t('Billing Address')}</h3>
                </div>
                <>
                    {orderModificationMode ? (
                        <div className="billingaddress__description" data-qm-encrypt="true">
                            {cartData &&
                            orderHistoryDetail &&
                            orderHistoryDetail.ShipLevels &&
                            orderHistoryDetail.ShipLevels.length > 0 ? (
                                orderHistoryDetail.ShipLevels[0].ShipAddress == orderHistoryDetail.BillingAddress ? (
                                    <div>{t('Same as shipping address')}</div>
                                ) : (
                                    <div>
                                        <div>{orderHistoryDetail.CustomerName}</div>
                                        <div>
                                            <span>{orderHistoryDetail.BillingAddress}</span>
                                        </div>
                                        <div>
                                            <span> {orderHistoryDetail.BillingCityProvincePostalCode}</span>
                                        </div>
                                    </div>
                                )
                            ) : null}
                        </div>
                    ) : (
                        <div className="billingaddress__description" data-qm-encrypt="true">
                            {cartData && cartBillingAddress ? (
                                isBillingSameAsShipping ? (
                                    <div>{t('Same as shipping address')}</div>
                                ) : (
                                    <div>
                                        <div>{cartBillingAddress.FirstName + ' ' + cartBillingAddress.LastName}</div>
                                        <div>
                                            {cartBillingAddress.IsPOBox ? (
                                                <span> {cartBillingAddress.POBoxNumber} </span>
                                            ) : (
                                                <span>{cartBillingAddress.Street}</span>
                                            )}
                                        </div>
                                        <div>
                                            {cartBillingAddress.AddressRef1 && (
                                                <span> {cartBillingAddress.AddressRef1}</span>
                                            )}
                                        </div>
                                        <div>
                                            {cartBillingAddress.City +
                                                ', ' +
                                                cartBillingAddress.State +
                                                ' ' +
                                                cartBillingAddress.ZipCode}
                                        </div>
                                        <div> {phoneNumber}</div>
                                    </div>
                                )
                            ) : null}
                        </div>
                    )}
                </>

                <>
                    {!orderModificationMode && (
                        <button
                            className="button__change"
                            onClick={openModal}
                            data-link-type="link"
                            data-link-title="billing address"
                            data-component-type="express checkout"
                        >
                            {t('Change')}
                        </button>
                    )}
                </>
            </div>

            <Modal
                isOpen={modalIsOpen}
                contentLabel="Change Billing Address"
                className="modal__contentwrap"
                overlayClassName="modal__overlay"
            >
                <div className="modal__header">
                    <button className="modal__button-back" onClick={closeModal}>
                        {t('Back')}
                    </button>
                    <h3 className="modal__title">{t('Change Billing Address')}</h3>
                    <button className="modal__button-close" onClick={closeModal}>
                        {t('Close')}
                    </button>
                </div>
                <div className="modal__body">
                    {billingAddressErrorMessage && (
                        <div className="alert alert-danger">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                className="form__error-svg"
                            >
                                <path d="M0 0h24v24H0z" fill="none"></path>
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"></path>
                            </svg>
                            {billingAddressErrorMessage}
                        </div>
                    )}
                    {cartData && cartBillingAddress && (
                        <AddressForm
                            selectedAddress={cartBillingAddress}
                            setSelectedAddress={setAddressToEdit}
                            formState={formState}
                            setFormState={setFormState}
                            setErrorMessage={setErrorMessage}
                        />
                    )}
                </div>
                <div className="modal__footer">
                    <Loading />
                    <button className="modal__button--save button" onClick={validateFormAndSubmit}>
                        {t('Save & Continue')}
                    </button>
                </div>
            </Modal>
        </>
    );
};
